import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { useSelectedRowSnapshotsStore } from "@/components/query-table/stores/selectedRowSnapshotsStore";
import { useSelectedRowIds } from "@/components/query-table/hooks/useSelectedRowIds";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { useSelectionStaleness } from "@/modules/contractorsPlannedHoursWizard/hooks/useSelectionStaleness";
import { ContractorsPlannedHoursWizardService } from "@/services/contractorsPlannedHoursWizardService";
import { ApiError } from "@/services/api";
import {
  CalculateContractorsPlannedHoursWizardRequest,
  DisplayContractorsPlannedHoursWizardAllocation,
  ReadContractorsPlannedHoursWizardResult,
} from "@/types/contractorsPlannedHoursWizard";
import { useCallback, useState } from "react";

export type ContractorsPlannedHoursWizardTabId = "selecting" | "results";

export function useContractorsPlannedHoursWizardTabs() {
  const [calculationResults, setCalculationResults] = useState<
    ReadContractorsPlannedHoursWizardResult[]
  >([]);
  const [calculationSnapshot, setCalculationSnapshot] = useState<
    DisplayContractorsPlannedHoursWizardAllocation[]
  >([]);
  const [activeTab, setActiveTab] =
    useState<ContractorsPlannedHoursWizardTabId>("selecting");

  const { store: rowSelectionStore } = useRowSelectionContext();
  const selectedIds = useSelectedRowIds({});
  const {
    markSelectionRequested,
    markSelectionCalculated,
    hasUnappliedSelectionChanges,
    hasCalculatedAtLeastOnce,
    resetStaleness,
  } = useSelectionStaleness(selectedIds);

  const calculateMutation = useMutationWithErrorHandling<
    ReadContractorsPlannedHoursWizardResult[],
    ApiError,
    CalculateContractorsPlannedHoursWizardRequest,
    unknown
  >({
    mutationFn: ContractorsPlannedHoursWizardService.calculate,
    onSuccess: (data) => {
      setCalculationResults(data);
      markSelectionCalculated();
      setActiveTab("results");
    },
  });

  const handleCalculate = useCallback(() => {
    const uniqueIds = [...new Set(selectedIds)];
    if (uniqueIds.length === 0) {
      return;
    }
    const rows = useSelectedRowSnapshotsStore
      .getState()
      .getSnapshotRowsForIds(
        "contractorsPlannedHoursWizardAllocations",
        uniqueIds,
      ) as DisplayContractorsPlannedHoursWizardAllocation[];
    setCalculationSnapshot(rows.map((r) => ({ ...r })));
    markSelectionRequested(uniqueIds);
    calculateMutation.mutate({ schedules: uniqueIds });
  }, [selectedIds, calculateMutation, markSelectionRequested]);

  const handleClear = useCallback(() => {
    rowSelectionStore.clear();
    setCalculationResults([]);
    setCalculationSnapshot([]);
    resetStaleness();
    setActiveTab("selecting");
  }, [rowSelectionStore, resetStaleness]);

  const calculateDisabled =
    selectedIds.size === 0 ||
    calculateMutation.isPending ||
    (hasCalculatedAtLeastOnce && !hasUnappliedSelectionChanges);

  const clearDisabled =
    calculateMutation.isPending ||
    (selectedIds.size === 0 &&
      !hasCalculatedAtLeastOnce &&
      calculationSnapshot.length === 0);

  return {
    activeTab,
    setActiveTab,
    calculationResults,
    calculationSnapshot,
    calculateMutation,
    handleCalculate,
    handleClear,
    calculateDisabled,
    clearDisabled,
    hasUnappliedSelectionChanges,
  };
}
